const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (ch) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
}[ch]));

// document.title is swapped for the duration of a print job (see below), so
// only one job can safely be in flight at a time: a second call would capture
// the first job's swapped title as `previousTitle` and restore the wrong
// value on cleanup.
let printJobInFlight = false;

// Prints (or, via the browser's print dialog, saves as PDF) a DOM node in an
// off-screen iframe so we don't disturb the current page's layout/scroll
// state and don't need a PDF-generation dependency.
//
// Returns a promise that resolves once the job finishes (or immediately, if
// there was nothing to print or a job was already in flight), so callers can
// track in-flight state — e.g. to disable print/save buttons for its duration.
export default function printDocument(node, { title = document.title, extraStyles = '' } = {}) {
    if (!node || printJobInFlight) return Promise.resolve();
    printJobInFlight = true;

    // Clone rather than reading node.outerHTML on the live node directly: a
    // controlled input (React or otherwise) sets the DOM `.value` property
    // without ever writing the `value` HTML attribute, so outerHTML on the
    // live node serializes a typed value (e.g. a typed e-signature) as
    // blank even though the screen shows it. Sync each clone's attribute
    // from the live field's property before serializing — this never
    // touches the live, framework-managed DOM.
    const printableNode = node.cloneNode(true);
    const liveFields = node.querySelectorAll('input, textarea, select');
    const clonedFields = printableNode.querySelectorAll('input, textarea, select');
    liveFields.forEach((liveEl, index) => {
        const clonedEl = clonedFields[index];
        if (!clonedEl) return;
        if (liveEl.tagName === 'TEXTAREA') {
            clonedEl.textContent = liveEl.value;
        } else if (liveEl.tagName === 'SELECT') {
            Array.from(clonedEl.options).forEach((opt, i) => {
                opt.selected = liveEl.options[i]?.selected ?? false;
            });
        } else {
            clonedEl.setAttribute('value', liveEl.value);
        }
    });

    return new Promise((resolve) => {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.top = '-10000px';
        iframe.style.left = '-10000px';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);

        // Chrome/Edge derive the "Save as PDF" suggested filename from the top-level
        // tab's document.title, not the iframe's — even though print() is called on
        // the iframe's window. Swap it in for the duration of the print job.
        const previousTitle = document.title;

        const cleanup = () => {
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe);
                document.title = previousTitle;
            }
            printJobInFlight = false;
            resolve();
        };

        const styleTags = Array.from(
            document.querySelectorAll('link[rel="stylesheet"], style'),
        ).map((el) => el.outerHTML).join('\n');

        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>${escapeHtml(title)}</title>${styleTags}<style>body{margin:0;padding:32px;background:#fff;}${extraStyles}</style></head><body>${printableNode.outerHTML}</body></html>`);
        iframeDoc.close();

        iframe.onload = () => {
            const win = iframe.contentWindow;
            if (!win) {
                cleanup();
                return;
            }
            document.title = title;

            let finished = false;
            let fallbackTimer;
            const finish = () => {
                if (finished) return;
                finished = true;
                window.clearTimeout(fallbackTimer);
                cleanup();
            };

            win.addEventListener('afterprint', finish);
            // Safari and some older browsers don't reliably fire `afterprint` on iframe windows.
            fallbackTimer = window.setTimeout(finish, 60000);
            win.focus();
            win.print();
        };
    });
}
